/**
 * «Продажи» — API-клиент. Контракт — docs/SPEC_2026-10-20_ANALYTICS_WAVE2_SALES.md, раздел 3.3.
 * Числа с сервера приходят и числами, и строками (DECIMAL из MySQL), поэтому все метрики приводятся к
 * `number | null`; пустое остаётся `null`, а не превращается в ноль.
 */

import { api } from '../../../shared/api/client'
import { buildQuery, unwrap } from '../shared/api'
import { readCoverage } from '../shared/compare'
import { toNumberOrNull } from '../shared/format'
import type { AnalyticsFilters } from '../shared/types'
import {
  SALES_METRIC_KEYS,
  type BreakdownGroup,
  type BreakdownItem,
  type HourlyCell,
  type MatrixMetric,
  type MatrixRow,
  type SalesBreakdown,
  type SalesFreshness,
  type SalesHourly,
  type SalesMatrix,
  type SalesMetricKey,
  type SalesMetrics,
  type SalesSeries,
  type SalesSeriesPoint,
  type SalesSummary,
  type SeriesGranularity,
  type SeriesMetric,
} from './types'

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : [])
const textOrNull = (value: unknown): string | null =>
  value === null || value === undefined || value === '' ? null : String(value)

export function normalizeMetrics(raw: any): SalesMetrics {
  const result = {} as Record<string, number | null>
  for (const key of SALES_METRIC_KEYS) result[key] = toNumberOrNull(raw?.[key])
  return result as unknown as SalesMetrics
}

const normalizeOptionalMetrics = (raw: any): SalesMetrics | null =>
  raw === null || raw === undefined ? null : normalizeMetrics(raw)

function normalizeFreshness(raw: any): SalesFreshness {
  return {
    wbOrders: textOrNull(raw?.wbOrders),
    wbSales: textOrNull(raw?.wbSales),
    ozonOrders: textOrNull(raw?.ozonOrders),
    funnel: textOrNull(raw?.funnel),
  }
}

/** Пометки определений метрик сводки (`notes.buyoutPercent`): только известные ключи и непустые строки. */
export function normalizeSalesNotes(raw: unknown): Partial<Record<SalesMetricKey, string>> {
  if (!raw || typeof raw !== 'object') return {}
  const notes: Partial<Record<SalesMetricKey, string>> = {}
  for (const key of SALES_METRIC_KEYS) {
    const value = textOrNull((raw as any)[key])
    if (value) notes[key] = value
  }
  return notes
}

export async function getSalesSummary(filters: AnalyticsFilters): Promise<SalesSummary> {
  const payload = unwrap(await api.get('/analytics/sales/summary', { params: buildQuery(filters) })) ?? {}
  return {
    current: normalizeMetrics(payload.current),
    previous: normalizeOptionalMetrics(payload.previous),
    freshness: normalizeFreshness(payload.freshness),
    previousCoverage: readCoverage(payload),
    notes: normalizeSalesNotes(payload.notes),
  }
}

function normalizeSeriesPoints(raw: unknown): SalesSeriesPoint[] {
  return asArray<any>(raw).map((point) => ({
    date: String(point.date),
    value: toNumberOrNull(point.value),
    previousValue: toNumberOrNull(point.previousValue),
  }))
}

function normalizeSeries(payload: any, withMetrics: boolean): SalesSeries {
  const byMetric: Partial<Record<SalesMetricKey, SalesSeriesPoint[]>> = {}
  if (withMetrics && payload?.seriesByMetric && typeof payload.seriesByMetric === 'object') {
    for (const key of SALES_METRIC_KEYS) {
      if (Array.isArray(payload.seriesByMetric[key])) byMetric[key] = normalizeSeriesPoints(payload.seriesByMetric[key])
    }
  }
  return {
    points: normalizeSeriesPoints(payload?.points),
    ...(withMetrics ? { seriesByMetric: byMetric } : {}),
    events: asArray<any>(payload.events).map((event) => ({
      date: String(event.date),
      type: String(event.type ?? ''),
      label: String(event.label ?? ''),
    })),
    previousCoverage: readCoverage(payload),
  }
}

export async function getSalesSeries(
  filters: AnalyticsFilters,
  options: { granularity: SeriesGranularity; metric: SeriesMetric },
): Promise<SalesSeries> {
  const payload = unwrap(await api.get('/analytics/sales/series', { params: buildQuery(filters, options) })) ?? {}
  return normalizeSeries(payload, false)
}

/** Ряды нескольких метрик одним запросом (`metrics=a,b,c`): плитки страницы берут спарклайны сразу все. */
export async function getSalesSeriesMany(
  filters: AnalyticsFilters,
  options: { granularity: SeriesGranularity; metrics: SeriesMetric[] },
): Promise<SalesSeries> {
  const payload = unwrap(await api.get('/analytics/sales/series', { params: buildQuery(filters, { granularity: options.granularity, metrics: options.metrics.join(',') }) })) ?? {}
  return normalizeSeries(payload, true)
}

export interface BreakdownQuery {
  groupBy: BreakdownGroup
  sort: string
  dir: 'asc' | 'desc'
  page: number
  limit: number
}

function normalizeBreakdownItem(raw: any): BreakdownItem {
  const key = raw?.key
  return {
    key: key === null || key === undefined || key === '' ? null : String(key),
    label: String(raw?.label ?? ''),
    sublabel: textOrNull(raw?.sublabel),
    imageUrl: textOrNull(raw?.imageUrl),
    designCode: textOrNull(raw?.designCode),
    metrics: normalizeMetrics(raw?.metrics),
    previous: normalizeOptionalMetrics(raw?.previous),
    share: toNumberOrNull(raw?.share),
  }
}

export async function getSalesBreakdown(filters: AnalyticsFilters, query: BreakdownQuery): Promise<SalesBreakdown> {
  const payload = unwrap(await api.get('/analytics/sales/breakdown', { params: buildQuery(filters, { ...query }) })) ?? {}
  return {
    items: asArray<any>(payload.items).map(normalizeBreakdownItem),
    total: toNumberOrNull(payload.total) ?? 0,
    totals: normalizeMetrics(payload.totals),
    previousTotals: normalizeOptionalMetrics(payload.previousTotals),
    previousCoverage: readCoverage(payload),
  }
}

/**
 * «Когда заказывают»: заказы по дням недели и часам (МСК) за период с общими фильтрами.
 * Контракт — SPEC_2026-10-20_ANALYTICS_WAVE3_ASSORTMENT.md, раздел 5, п. 3 бэкенда.
 */
export async function getSalesHourly(filters: AnalyticsFilters): Promise<SalesHourly> {
  const payload = unwrap(await api.get('/analytics/sales/heatmap-hourly', { params: buildQuery(filters) })) ?? {}
  const cells: HourlyCell[] = asArray<any>(payload.cells)
    .map((cell) => ({
      weekday: Number(cell?.weekday),
      hour: Number(cell?.hour),
      ordersQty: toNumberOrNull(cell?.ordersQty),
      ordersRub: toNumberOrNull(cell?.ordersRub),
    }))
    .filter((cell) => Number.isInteger(cell.weekday) && cell.weekday >= 1 && cell.weekday <= 7 && Number.isInteger(cell.hour) && cell.hour >= 0 && cell.hour <= 23)
  return { cells, totalQty: toNumberOrNull(payload.totalQty) }
}

export async function getSalesMatrix(
  filters: AnalyticsFilters,
  options: { groupBy: BreakdownGroup; metric: MatrixMetric; weeks: number },
): Promise<SalesMatrix> {
  const payload = unwrap(await api.get('/analytics/sales/matrix', { params: buildQuery(filters, options) })) ?? {}
  const rows: MatrixRow[] = asArray<any>(payload.rows).map((row) => ({
    key: row?.key === null || row?.key === undefined || row?.key === '' ? null : String(row.key),
    label: String(row?.label ?? ''),
    values: asArray<unknown>(row?.values).map(toNumberOrNull),
    total: toNumberOrNull(row?.total),
  }))
  const total = payload.total
  return {
    weeks: asArray<unknown>(payload.weeks).map(String),
    rows,
    weekTotals: Array.isArray(total) ? total.map(toNumberOrNull) : null,
    grandTotal: Array.isArray(total) ? null : toNumberOrNull(total),
  }
}
